import { useQuery } from '@tanstack/react-query'
import { api } from './client'

export interface SiteToday {
  siteId: string
  siteCode: string
  siteName: string
  projectName?: string
  /** Null when nobody has opened the day yet — the most useful thing on the screen. */
  reportId?: string | null
  reportStatus?: string | null
  editable: boolean
  workLines: number
  labourLines: number
  workers: number
  materialReceipts: number
  started: boolean
  complete: boolean
}

export interface DashboardSummary {
  date: string
  siteCount: number
  sitesStarted: number
  sitesComplete: number
  workersOnSite: number
  pendingDrawings: number
  overdueDrawings: number
  sites: SiteToday[]
}

/**
 * The whole dashboard in one request.
 *
 * <p>It used to be assembled here out of four or five calls, which is slow on a site
 * connection and gave the web and the phone two slightly different answers. The server
 * now computes it once, inside the same access rules everything else obeys.</p>
 */
export function useDashboardSummary(date?: string) {
  return useQuery({
    queryKey: ['dashboard-summary', date ?? 'today'],
    queryFn: async () => {
      const { data } = await api.get<DashboardSummary>('/dashboard/summary', {
        params: date ? { date } : undefined,
      })
      return data
    },
    // The day moves on while the page is open; a stale count is worse than a refetch.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  })
}
